import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { useSession } from "@/services/auth0/session";

const API_URL = import.meta.env.VITE_API_URL;

export type ShipmentChangeRequestType =
  | "RECIPIENT_ADDRESS_CORRECTION"
  | "RECIPIENT_CONTACT_UPDATE"
  | "RECIPIENT_DELIVERY_INSTRUCTIONS"
  | "DELIVERY_RESCHEDULE"
  | "SENDER_ADDRESS_CORRECTION"
  | "SENDER_CONTACT_UPDATE"
  | "SENDER_PICKUP_INSTRUCTIONS"
  | "PICKUP_RESCHEDULE";

export type ShipmentChangeRequestStatus =
  | "PENDING"
  | "ACKNOWLEDGED"
  | "ACTIONED"
  | "CANNOT_FULFILL"
  | "AUTO_APPROVED";

export interface PendingShipmentChangeRequest {
  id: string;
  shipmentId: string;
  trackingNumber: string;
  shipmentNumber: number;
  submittedBy: {
    type: "CUSTOMER" | "CARRIER" | "ADMIN";
    id: string;
    name: string;
    email: string | null;
  };
  status: ShipmentChangeRequestStatus;
  requestTypes: ShipmentChangeRequestType[];
  rescheduleDate?: string;
  reschedulePreference?: string;
  addressCorrection?: string;
  addressLine2Correction?: string;
  deliveryInstructions?: string;
  contactPhone?: string;
  contactName?: string;
  contactEmail?: string;
  additionalNotes?: string;
  createdAt: string;
}

export interface ShipmentChangeRequestRecord {
  id: string;
  status: ShipmentChangeRequestStatus;
  submittedByActorType: "CUSTOMER" | "CARRIER" | "ADMIN";
  requestTypes: ShipmentChangeRequestType[];
  addressCorrection: string | null;
  addressLine2Correction: string | null;
  deliveryInstructions: string | null;
  contactName: string | null;
  contactPhone: string | null;
  contactEmail: string | null;
  appliedChanges: string[];
  createdAt: string;
  handledAt: string | null;
}

export function useShipmentChangeRequests(shipmentId: string | null) {
  const { token } = useSession();
  return useQuery<ShipmentChangeRequestRecord[]>({
    queryKey: ["shipment-change-requests", shipmentId],
    queryFn: async () => {
      const { data } = await axios.get<{
        requests: ShipmentChangeRequestRecord[];
      }>(`${API_URL}/api/v2/shipments/${shipmentId}/change-requests`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return data.requests;
    },
    enabled: Boolean(shipmentId && token),
  });
}

interface PendingShipmentChangeRequestsResponse {
  requests: PendingShipmentChangeRequest[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
  };
}

export interface UsePendingShipmentChangeRequestsOptions {
  carrierId?: string;
  dispatchCenterId?: string;
}

/**
 * Get pending shipment change requests for admin
 */
export function usePendingShipmentChangeRequests(
  limit = 50,
  offset = 0,
  options: UsePendingShipmentChangeRequestsOptions = {}
) {
  const { token } = useSession();
  const { carrierId, dispatchCenterId } = options;

  return useQuery<PendingShipmentChangeRequestsResponse>({
    queryKey: [
      "pendingShipmentChangeRequests",
      limit,
      offset,
      carrierId,
      dispatchCenterId,
    ],
    queryFn: async () => {
      const params = new URLSearchParams({
        limit: String(limit),
        offset: String(offset),
      });
      if (carrierId) params.append("carrierId", carrierId);
      if (dispatchCenterId) params.append("dispatchCenterId", dispatchCenterId);
      const { data } = await axios.get<PendingShipmentChangeRequestsResponse>(
        `${API_URL}/api/v2/change-requests?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return data;
    },
    enabled: !!token,
  });
}

export interface HandleShipmentChangeRequestParams {
  id: string;
  status: "ACTIONED" | "CANNOT_FULFILL";
  adminResponse?: string;
}

interface HandleShipmentChangeRequestResponse {
  success: boolean;
  message: string;
  request: {
    id: string;
    status: ShipmentChangeRequestStatus;
    adminResponse?: string;
    handledAt: string;
    appliedChanges: string[];
  };
}

/**
 * Handle a shipment change request (action or reject)
 */
export function useHandleShipmentChangeRequest() {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation<
    HandleShipmentChangeRequestResponse,
    Error,
    HandleShipmentChangeRequestParams
  >({
    mutationFn: async ({ id, ...body }) => {
      const { data } = await axios.patch<HandleShipmentChangeRequestResponse>(
        `${API_URL}/api/v2/change-requests/${id}`,
        body,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["pendingShipmentChangeRequests"],
      });
      await queryClient.invalidateQueries({ queryKey: ["shipments"] });
      await queryClient.invalidateQueries({ queryKey: ["shipment"] });
      await queryClient.invalidateQueries({
        queryKey: ["shipment-change-requests"],
      });
    },
  });
}

export interface CreateShipmentChangeRequestParams {
  shipmentId: string;
  requestTypes: ShipmentChangeRequestType[];
  rescheduleDate?: string;
  reschedulePreference?: "morning" | "afternoon" | "evening";
  addressCorrection?: string;
  addressLine2Correction?: string;
  deliveryInstructions?: string;
  contactPhone?: string;
  contactName?: string;
  contactEmail?: string;
  additionalNotes?: string;
}

interface CreateShipmentChangeRequestResponse {
  success: boolean;
  message: string;
  request: {
    id: string;
    status: ShipmentChangeRequestStatus;
    requestTypes: ShipmentChangeRequestType[];
    createdAt: string;
  };
  autoApproved: boolean;
  appliedChanges?: string[];
}

/**
 * Create a shipment change request
 */
export function useCreateShipmentChangeRequest() {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation<
    CreateShipmentChangeRequestResponse,
    Error,
    CreateShipmentChangeRequestParams
  >({
    mutationFn: async ({ shipmentId, ...body }) => {
      const { data } = await axios.post<CreateShipmentChangeRequestResponse>(
        `${API_URL}/api/v2/shipments/${shipmentId}/change-requests`,
        body,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["pendingShipmentChangeRequests"],
      });
    },
  });
}

/**
 * Format request type for display
 */
export function formatShipmentChangeRequestType(
  type: ShipmentChangeRequestType
): string {
  const labels: Record<ShipmentChangeRequestType, string> = {
    RECIPIENT_ADDRESS_CORRECTION: "Recipient address correction",
    RECIPIENT_CONTACT_UPDATE: "Update recipient contact",
    RECIPIENT_DELIVERY_INSTRUCTIONS: "Delivery instructions",
    DELIVERY_RESCHEDULE: "Reschedule delivery",
    SENDER_ADDRESS_CORRECTION: "Sender address correction",
    SENDER_CONTACT_UPDATE: "Update sender contact",
    SENDER_PICKUP_INSTRUCTIONS: "Pickup instructions",
    PICKUP_RESCHEDULE: "Reschedule pickup",
  };
  return labels[type] || type;
}

/**
 * Format status for display
 */
export function formatShipmentChangeRequestStatus(
  status: ShipmentChangeRequestStatus
): string {
  const labels: Record<ShipmentChangeRequestStatus, string> = {
    PENDING: "Pending",
    ACKNOWLEDGED: "Acknowledged",
    ACTIONED: "Handled",
    CANNOT_FULFILL: "Cannot Fulfill",
    AUTO_APPROVED: "Auto-Approved",
  };
  return labels[status] || status;
}
